import { z } from 'zod';

import { assertOk, parseJson, ApiResponseError } from './response';

type ApiFetchFn = typeof import('./requests')['apiFetch'];

const resolveApiFetch = async (): Promise<ApiFetchFn> => {
  const globalRef = globalThis as typeof globalThis & {
    jest?: {
      requireMock?: (id: string) => unknown;
    };
  };

  const jestGlobal = globalRef.jest;
  if (jestGlobal && typeof jestGlobal.requireMock === 'function') {
    try {
      const mockedModule = jestGlobal.requireMock('@/lib/api/requests') as { apiFetch?: ApiFetchFn };
      if (mockedModule && typeof mockedModule.apiFetch === 'function') {
        return mockedModule.apiFetch;
      }
    } catch (error) {
      void error;
    }
  }

  const module = await import('./requests');
  return module.apiFetch;
};

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type JsonRequestOptions = {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

const hasHeader = (headers: Record<string, string>, name: string): boolean =>
  Object.keys(headers).some((key) => key.toLowerCase() === name.toLowerCase());

const serialiseBody = (body: unknown): BodyInit | undefined => {
  if (body === undefined || body === null) {
    return undefined;
  }
  if (typeof body === 'string') {
    return body;
  }
  if (body instanceof URLSearchParams) {
    return body;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return body;
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return body;
  }
  return JSON.stringify(body);
};

async function request(
  path: string,
  { method = 'GET', body, headers = {}, signal }: JsonRequestOptions = {},
): Promise<Response> {
  const apiFetch = await resolveApiFetch();
  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...headers,
  };

  const serialisedBody = serialiseBody(body);
  if (serialisedBody !== undefined && !hasHeader(requestHeaders, 'content-type') && typeof serialisedBody === 'string') {
    requestHeaders['Content-Type'] = 'application/json';
  }

  return apiFetch(path, {
    method,
    headers: requestHeaders,
    signal,
    body: serialisedBody,
  });
}

const ProvisionTenantPayloadSchema = z.object({
  email: z.string().email(),
  restaurantName: z.string().min(1),
  subdomain: z.string().min(1),
  tz: z.string().min(1),
  authUserId: z.string().min(1).optional(),
  password: z.string().min(1).optional(),
});

const ProvisionTenantResponseSchema = z
  .object({
    ok: z.literal(true),
    tenant: z
      .object({
        id: z.string(),
        name: z.string(),
        subdomain: z.string(),
        timezone: z.string(),
      })
      .passthrough(),
    location: z
      .object({
        id: z.string(),
        name: z.string(),
        timezone: z.string(),
      })
      .passthrough(),
    user: z
      .object({
        id: z.string(),
        email: z.string().email(),
        role: z.string(),
      })
      .passthrough(),
    checklist: z
      .object({
        next: z.array(z.string()),
        tables: z.array(
          z
            .object({
              id: z.string(),
              qrCodeSlug: z.string(),
              deepLink: z.string(),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  })
  .passthrough();

export type ProvisionTenantPayload = z.infer<typeof ProvisionTenantPayloadSchema>;
export type ProvisionTenantResponse = z.infer<typeof ProvisionTenantResponseSchema>;

export async function provisionTenant(payload: ProvisionTenantPayload): Promise<ProvisionTenantResponse> {
  const body = ProvisionTenantPayloadSchema.parse(payload);

  const response = await request('/onboarding/signup', {
    method: 'POST',
    body,
  });

  await assertOk(response);
  return parseJson(response, ProvisionTenantResponseSchema);
}

const SyncSessionPayloadSchema = z.object({
  authUserId: z.string().min(1),
  email: z.string().email().optional(),
});

const SyncSessionUserSchema = z
  .object({
    id: z.string(),
    email: z.string().email(),
    role: z.string(),
  })
  .passthrough();

const SyncSessionTenantSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string().optional(),
  })
  .passthrough();

const SyncSessionResponseSchema = z.object({
  ok: z.literal(true),
  session: z.object({
    authenticated: z.boolean(),
    user: SyncSessionUserSchema.optional(),
    tenant: SyncSessionTenantSchema.optional(),
  }),
});

const SyncSessionMissingSchema = z.object({
  ok: z.literal(false),
  missing: z.literal(true),
});

export type SyncSessionPayload = z.infer<typeof SyncSessionPayloadSchema>;
export type SyncSessionResponse = z.infer<typeof SyncSessionResponseSchema>;
export type SyncSessionMissingResponse = z.infer<typeof SyncSessionMissingSchema>;

export interface SyncSessionOptions {
  payload?: SyncSessionPayload;
  accessToken?: string | null | undefined;
}

export async function syncSession(
  options?: SyncSessionOptions,
): Promise<SyncSessionResponse | SyncSessionMissingResponse> {
  const accessToken = options?.accessToken;
  const headers: Record<string, string> = {};

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await request('/auth/sync', {
    method: 'POST',
    headers,
    body: options?.payload ? SyncSessionPayloadSchema.parse(options.payload) : undefined,
  });

  if (response.status === 404) {
    return SyncSessionMissingSchema.parse({ ok: false, missing: true });
  }

  await assertOk(response);
  return parseJson(response, SyncSessionResponseSchema);
}

const LogoutResponseSchema = z.object({ ok: z.boolean() }).passthrough();
export type LogoutResponse = z.infer<typeof LogoutResponseSchema>;

export async function terminateSession(): Promise<LogoutResponse> {
  const response = await request('/auth/logout', { method: 'POST' });

  if (response.status === 204) {
    return { ok: true };
  }

  await assertOk(response);

  try {
    return await parseJson(response, LogoutResponseSchema);
  } catch (error) {
    if (error instanceof ApiResponseError) {
      return { ok: true };
    }
    throw error;
  }
}
